import React, { useEffect, useState } from 'react';

// Local Resources
import { ANALYSIS_TYPE, MeasurementStatetoHTML, StatisticsSectionToHTML, AnalysisResultToIconState } from '../enums';

// MaterialUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

// Tippy.JS Resources
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';

// Default image size parameters
const defaultWidth = 64;
const defaultHeight = 70;

// Min and Max AnalysisResult values
const MIN_ANALYSIS_RESULT = 0;
const MAX_ANALYSIS_RESULT = 7;

// String name to display on the icon
const STRING_NAME = 'String ';

// Function to build the popover content for the string icon
function BuildPopover(stringData, stringStats) {
	const stringVoltage = MeasurementStatetoHTML('String', stringData.status.voltage, ANALYSIS_TYPE.VOLTAGE, 1);
	const stringCurrent = MeasurementStatetoHTML('String', stringData.status.current, ANALYSIS_TYPE.CURRENT, 1);

	const unitsVoltage = StatisticsSectionToHTML(stringStats.voltage, ANALYSIS_TYPE.VOLTAGE);
	const unitsImpedance = StatisticsSectionToHTML(stringStats.impedance, ANALYSIS_TYPE.IMPEDANCE);
	const unitsTemperature = StatisticsSectionToHTML(stringStats.temperature, ANALYSIS_TYPE.TEMPERATURE);
	const unitsDiffTemperature = StatisticsSectionToHTML(stringStats.diff_temperature, ANALYSIS_TYPE.DIFF_TEMPERATURE);

	const labelText = stringData.string_label?.trim() !== '' ? ` - ${stringData.string_label}` : '';

	return (
		<div
			style={{
				fontFamily: 'Saira, sans-serif',
				fontWeight: '600',
				padding: '10px',
				maxWidth: '300px',
			}}
		>
			{/* String Name */}
			<p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
				{STRING_NAME}{stringData.string_num}{labelText}
			</p>

			{/* String Analyses */}
			<div style={{ marginBottom: '15px', marginLeft: '10px' }}>
				{stringVoltage} <br />
				{stringCurrent}
			</div>

			{/* Divider */}
			<hr style={{ border: 'none', borderTop: '1px solid gray', margin: '10px 0' }} />

			{/* Statistics Section */}
			<div>
				<p style={{ fontWeight: 'bold', color: 'lightgray', marginBottom: '5px' }}>
					Unit Statistics:
				</p>

				{/* Voltage */}
				<div style={{ marginBottom: '15px' }}>
					<div style={{ paddingLeft: '10px' }}>{unitsVoltage}</div>
				</div>

				{/* Impedance */}
				<div style={{ marginBottom: '15px' }}>
					<div style={{ paddingLeft: '10px' }}>{unitsImpedance}</div>
				</div>

				{/* Temperature */}
				<div style={{ marginBottom: '15px' }}>
					<div style={{ paddingLeft: '10px' }}>{unitsTemperature}</div>
				</div>

				{/* Differential Temperature */}
				<div>
					<div style={{ paddingLeft: '10px' }}>{unitsDiffTemperature}</div>
				</div>
			</div>
		</div>
	);
}

// Function to process statistics and return counts and max values for each statistic section
function processStatistics(statistics) {
	const result = {};

	// Iterate over each statistic section
	for (const [key, values] of Object.entries(statistics)) {

		// Initialize counts for values 0 to 7
		const counts = Array(8).fill(0);

		// Count occurrences and find the maximum value
		let maxValue = -Infinity;
		values.forEach(value => {
			if (value >= MIN_ANALYSIS_RESULT && value <= MAX_ANALYSIS_RESULT) {
				counts[value]++;
			}

			if (value > maxValue) {
				maxValue = value;
			}
		});

		result[key] = { counts, maxValue };
	}

	return result;
}

export default function StringIcon(props) {

	const {isActive, stringData, onClick, scale = 1.0} = props;

	const stringStats = processStatistics(stringData.statistics);

	// Get the worst state of the string between the overall status, string measurements statuses, and the max values of the unit analyses
	const worstState = Math.max(
		0,
		stringData.status.overall,
		stringData.status.voltage.result,
		stringData.status.current.result,
		stringStats.voltage.maxValue,
		stringStats.impedance.maxValue,
		stringStats.temperature.maxValue,
		stringStats.diff_temperature.maxValue,
	);

	const iconState = AnalysisResultToIconState(worstState);
	const imgPath = `/images/batterymap_icons/string_${iconState}.svg`;
	
	// Set visual formatting based on string selection status
	const borderColor = isActive ? '#B1282EFF' : '#FFFFFF11';
	const borderWidth = isActive ? '3px' : '1px';
	const buttonMargin = isActive ? '-1px' : '1px';

	const popover = BuildPopover(stringData, stringStats)

	return (
		<Tippy
			content={popover}
			placement="bottom"
			animation="shift-away"
		>
			<Button
				sx={{
					padding: 0,
					margin: '3px',
				}}
				onClick={() => onClick(stringData.string_num)}
			>
				<Box
					border='solid'
					borderRadius={0.75}
					borderColor={borderColor}
					bgcolor='#FFFFFF22'
					padding='3px'
					margin={buttonMargin}
					sx={{
						borderWidth: borderWidth,
						'&:hover': {
							color: '#FFFFFF',
							backgroundColor: '#FFFFFF44',
						},
					}}
				>
					<img src={imgPath} width={defaultWidth * scale} height={defaultHeight * scale} alt={`${STRING_NAME}${stringData.string_num} icon`} />
					<Typography
						fontFamily='Helvetica Neue LT Std Roman, sans-serif'
						color='#FFFFFF'
						align='center'
						sx={{ fontSize: `calc(1rem * ${scale})` }}
					>
						{STRING_NAME}{stringData.string_num}
					</Typography>
				</Box>
			</Button>
		</Tippy>
	);
}